import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import Sidebar from './components/Sidebar';
import SearchBar from './components/SearchBar';
import ResultsList from './components/ResultsList';
import UploadForm from './components/UploadForm';
import TalentVault from './components/TalentVault';
import Insights from './components/Insights';
import Settings from './components/Settings';
import CandidateDetails from './components/CandidateDetails';
import Login from './components/Login';
import Profile from './components/Profile';
import AdminDashboard from './components/AdminDashboard'; // Import Admin
import SetupPassword from './components/SetupPassword'; // Import Setup

import ChatWidget from './components/ChatWidget';
import JobsDashboard from './components/JobsDashboard';
import JobDetail from './components/JobDetail';
import { motion, AnimatePresence } from 'framer-motion';
import { FileSearch } from 'lucide-react';
import axios from 'axios';

import { API_BASE } from './config';

// const API_BASE = "http://127.0.0.1:8000";


function App() {
  // Check for Setup Password Route first (Public)
  const isSetupRoute = window.location.pathname === '/setup-password';

  // Auth State
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('token');
  });

  // User State (Lifted)
  const [user, setUser] = useState(null);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch (err) {
      console.error("Failed to fetch user", err);
      if (err.response?.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('token');
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUser();
    }
  }, [isAuthenticated]);

  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('activeTab');
    if (saved === 'job-detail') return 'jobs';
    return saved || 'discover';
  });

  // Persist activeTab to localStorage so reload preserves navigation
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  // Data State
  const [results, setResults] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [vaultSearchTerm, setVaultSearchTerm] = useState('');

  // Dark Mode State
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' ||
        (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handlePreview = (id, name, fullObj = null) => {
    if (fullObj) {
      setSelectedCandidate(fullObj);
    } else {
      console.warn("Missing full candidate object for preview");
      setSelectedCandidate({
        _id: id,
        name: name,
        email: 'Loading...',
        skills: [],
        years_experience: 0
      });
    }
  };


  const handleResultPreview = (id, name) => {
    // This function already tries to find it in 'results' state
    const candidate = results.find(r => r._id === id || r.resume_id === id);
    handlePreview(id, name, candidate);
  };

  const handleDelete = (id) => {
    setResults(prev => prev.filter(r => r._id !== id));
  };

  // Special Route Handling
  if (isSetupRoute) {
    return (
      <>
        <Toaster position="top-right" />
        <SetupPassword />
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Toaster position="top-right" />
        <Login onLogin={() => setIsAuthenticated(true)} />
      </>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      // ... (keep jobs, job-detail, discover)
      case 'jobs':
        return (
          <JobsDashboard
            onViewJob={(job) => {
              setSelectedJob(job);
              setActiveTab('job-detail');
            }}
          />
        );

      case 'job-detail':
        return (
          <JobDetail
            job={selectedJob}
            onBack={() => setActiveTab('jobs')}
            onPreview={handlePreview}
          />
        );

      case 'discover':
        return (
          <div className="space-y-6 max-w-4xl mx-auto">
            <div className="text-center py-10">
              <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-4 tracking-tight">
                Find Your Next Star
              </h1>
              <p className="text-slate-400 text-lg">Search effectively across your resume database.</p>
            </div>

            <SearchBar setResults={setResults} setDebugInfo={setDebugInfo} />

            {/* DEBUG FILTER VIEW - Mediator Visualization */}
            {debugInfo && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg p-3 flex items-center flex-wrap gap-4 text-sm"
              >
                <span className="font-bold text-brand-700 dark:text-brand-300 flex items-center gap-2">
                  <span className="text-xl">🤖</span> AI Parsed Filters:
                </span>

                {debugInfo.skills && debugInfo.skills.length > 0 && (
                  <span className="bg-white dark:bg-slate-800 px-2 py-1 rounded shadow-sm flex items-center gap-1">
                    <span className="text-slate-500 text-xs uppercase">Skills:</span>
                    <span className="font-mono text-brand-600 dark:text-brand-400 font-semibold">{debugInfo.skills.join(', ')}</span>
                    <span className="mx-1 text-slate-400 text-xs">({debugInfo.skills_mode || 'any'})</span>
                  </span>
                )}

                {debugInfo.min_years && (
                  <span className="bg-white dark:bg-slate-800 px-2 py-1 rounded shadow-sm flex items-center gap-1">
                    <span className="text-slate-500 text-xs uppercase">Min Exp:</span>
                    <span className="font-mono text-brand-600 dark:text-brand-400 font-semibold">{debugInfo.min_years}y</span>
                  </span>
                )}

                {debugInfo.max_years && (
                  <span className="bg-white dark:bg-slate-800 px-2 py-1 rounded shadow-sm flex items-center gap-1">
                    <span className="text-slate-500 text-xs uppercase">Max Exp:</span>
                    <span className="font-mono text-brand-600 dark:text-brand-400 font-semibold">{debugInfo.max_years}y</span>
                  </span>
                )}

                {!debugInfo.skills && !debugInfo.min_years && !debugInfo.max_years && (
                  <span className="text-slate-500 italic">No specific filters detected (Semantic Search only)</span>
                )}
              </motion.div>
            )}

            <AnimatePresence mode='wait'>
              {results.length > 0 ? (
                <ResultsList
                  results={results}
                  onPreview={handleResultPreview}
                  onDelete={handleDelete}
                  searchedSkills={debugInfo?.skills || []}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-12 opacity-50">
                  <FileSearch className="w-16 h-16 text-slate-300 dark:text-slate-700 mb-4" />
                  <p className="text-slate-400">Search results will appear here</p>
                </div>
              )}
            </AnimatePresence>
          </div>
        );

      case 'vault':
        return <TalentVault
          searchTerm={vaultSearchTerm}
          setSearchTerm={setVaultSearchTerm}
          onPreview={(id, name, candidate) => {
            handlePreview(id, name, candidate);
          }}
        />;

      case 'import':
        return (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="text-center py-8">
              <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-4 tracking-tight">
                Import Candidates
              </h1>
              <p className="text-slate-400 text-lg">Upload PDF or DOCX resumes to populate your database.</p>
            </div>
            <UploadForm />
          </div>
        );

      case 'insights':
        return <Insights />;

      case 'settings':
        return <Settings darkMode={darkMode} toggleDarkMode={toggleDarkMode} />;

      case 'profile':
        return <Profile user={user} refreshUser={fetchUser} />;

      case 'admin':
        // Protect at render level too
        if (user?.role !== 'admin') return <div>Access Denied</div>;
        return <AdminDashboard />;

      default:
        return <div>Select a menu item</div>;
    }
  };

  return (
    <div className="min-h-screen font-sans bg-slate-50 text-slate-800 dark:bg-slate-900 dark:text-slate-100 transition-colors duration-300 relative">
      <Toaster position="top-right" toastOptions={{ className: 'dark:bg-slate-800 dark:text-white' }} />

      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} user={user} />

      <main className="lg:pl-64 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderContent()}
          </motion.div>
        </div>
      </main>

      <ChatWidget onPreview={handlePreview} />
      {(() => {
        const getSearchedSkills = () => {
          if (activeTab === 'discover') return debugInfo?.skills || [];
          if (activeTab === 'vault') return vaultSearchTerm ? [vaultSearchTerm] : [];
          if (activeTab === 'job-detail') return selectedJob?.requirements || [];
          return [];
        };

        const searchedSkills = getSearchedSkills();

        return selectedCandidate && (
          <CandidateDetails
            resume={selectedCandidate}
            onClose={() => setSelectedCandidate(null)}
            searchedSkills={searchedSkills}
          />
        );
      })()}
    </div>
  );
}

export default App;
